import React, {useEffect, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import LaneAliasCameras from "./components/lane-alias-cameras.jsx";
import {FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaPlus} from "react-icons/fa";
import {useParams} from "react-router";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import laneAliasService from "../../../../services/web/lane-alias-service.js";
import projectUtils from "../../../../utils/project-utils.js";

const LaneAliasForm = () => {
    const {id} = useParams();
    const navigate = navigateHook();

    const [name, setName] = useState("");

    const [cameras, setCameras] = useState([]);
    const [camerasSelected, setCamerasSelected] = useState([]);

    const camerasRef = useRef(undefined);
    const camerasSelectedRef = useRef(undefined);

    const {data: lanes} = useQuery({
        queryFn: () => laneAliasService.lanes(),
        queryKey: ['lanes'],
        enabled: !id,
    });

    const {data: lanesById, refetch} = useQuery({
        queryFn: () => laneAliasService.get(id).then(data => {
            data.all_lanes = data.all_lanes.filter(al => !data.lanes?.some(l => al.code === l.code))

            return data
        }),
        queryKey: ['get'],
        enabled: !!id,
    });

    useEffect(() => {
        if (!!id && lanesById) {
            setName(lanesById.name);
            setCamerasSelected(lanesById.lanes);
            setCameras(lanesById.all_lanes)
        }
    }, [id, lanesById]);

    useEffect(() => {
        if (!id && lanes) {
            setName("");
            setCameras(lanes)
            setCamerasSelected([]);
        }
    }, [id, lanes]);

    const moveAllToRight = () => {
        const nodaData = [];
        camerasRef.current?.api.forEachNodeAfterFilter(({data}) => { nodaData.push(data); });

        camerasSelectedRef.current.api.applyTransaction({ add: nodaData,  addIndex: undefined });
        camerasRef.current.api.applyTransaction({ remove: nodaData });
    }

    const moveToRight = (node = undefined) => {
        const selectedRows = !node ? camerasRef.current?.api?.getSelectedRows?.() ?? [] : [node];

        if (selectedRows.length) {
            camerasSelectedRef.current.api.applyTransaction({ add: selectedRows, addIndex: undefined });
            camerasRef.current.api.applyTransaction({ remove: selectedRows });
        }
    }

    const moveToLeft = (node = undefined) => {
        const selectedRows = !node ? camerasSelectedRef.current?.api?.getSelectedRows?.() ?? [] : [node];

        if (selectedRows.length) {
            camerasRef.current.api.applyTransaction({ add: selectedRows, addIndex: undefined });
            camerasSelectedRef.current.api.applyTransaction({ remove: selectedRows });
        }
    }

    const moveAllToLeft = () => {
        const nodaData = [];
        camerasSelectedRef.current?.api.forEachNodeAfterFilter(({data}) => { nodaData.push(data); });

        camerasRef.current.api.applyTransaction({ add: nodaData,  addIndex: undefined });
        camerasSelectedRef.current.api.applyTransaction({ remove: nodaData });
    }

    const submit = () => {
        const lanes = [];
        camerasSelectedRef?.current.api.forEachNode(({data}) => { lanes.push(data.code); });

        const form = {name, lanes}

        let response;
        if (id) {
            form.id = id;
            response = laneAliasService.edit(form);
        }
        else response = laneAliasService.create(form);

        response
            .then((data) => {
                projectUtils.success(!id);

                if (id) {
                    refetch();
                    return;
                }

                const urlDev = `/lane-alias/form/${data}`;
                navigate(urlDev);
            })
            .catch(error => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = translateError(error.response.data.message ?? error.response.data);
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            });
    }

    const deleteLaneAlias = () => {
        projectUtils.swalMessage(
            "warning",
            "Excluir",
            `Excluir o grupo de seleção de câmeras "${name ?? id}"?`,
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (isConfirmed) {
                    laneAliasService.delete(id)
                        .then(() => {
                            projectUtils.success(true, "Excluído com sucesso");
                            navigate("/lane-alias");
                        })
                        .catch(error => {
                            console.error(error);
                            let message = 'Não foi possível excluir.';
                            if (error.response.status !== 500) {
                                message = translateError(error.response.data.message ?? error.response.data);
                            }

                            projectUtils.swalErrorMessage('', message, 'Ok');
                        });
                }
            }
        );
    }

    const translateError = (message) => {
        switch (message) {
            case 'name.already.registered':
                return 'Este \"nome\" já está sendo utilizado.';
            case 'laneAlias.invalid':
                if (!name) {
                    return 'O campo "Nome" é obrigatório.';
                }
                return 'Selecione uma câmera';
            default:
                return 'Não foi possível salvar.';
        }
    }

    return (
        <Template>
            <Template.Header>
                <Template.Title>Grupo de seleção de câmeras (SentryView)</Template.Title>

                {
                    id &&
                    <Button target={"default"} onClick={() => navigate("/lane-alias/form")}>
                        <FaPlus /> Adicionar grupo
                    </Button>
                }
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body>
                        <InputForm
                            id={"name"}
                            value={name}
                            label={"Nome *"}
                            onChange={({target}) => setName(target.value)}
                        />
                    </Card.Body>
                </Card>

                <article className={"flex justify-between gap-3"}>
                    <LaneAliasCameras
                        target={"camera"}
                        cameras={cameras}
                        title={"Câmeras disponíveis"}
                        gridRef={camerasRef}
                        moveTo={moveToRight}
                        secondGridRef={camerasSelectedRef}
                    />

                    <section className={"flex flex-col gap-3 items-center justify-center"}>
                        <Button target={"default"} onClick={moveAllToRight} className={"justify-items-center block! w-6.25!"}>
                            <FaAngleDoubleRight />
                        </Button>
                        <Button target={"default"} onClick={() => moveToRight()} className={"justify-items-center block! w-6.25!"}>
                            <FaAngleRight />
                        </Button>
                        <Button target={"default"} onClick={() => moveToLeft()} className={"justify-items-center block! w-6.25!"}>
                            <FaAngleLeft />
                        </Button>
                        <Button target={"default"} onClick={moveAllToLeft} className={"justify-items-center block! w-6.25!"}>
                            <FaAngleDoubleLeft />
                        </Button>
                    </section>

                    <LaneAliasCameras
                        target={"cameraSelected"}
                        cameras={camerasSelected}
                        title={"Câmeras selecionadas"}
                        gridRef={camerasSelectedRef}
                        moveTo={moveToLeft}
                        secondGridRef={camerasRef}
                    />
                </article>

                <Card>
                    <Card.Body className={"flex justify-end gap-2"}>
                        {
                            id &&
                            <Button target={"danger"} onClick={deleteLaneAlias} actionFeedbackType='delete'>

                                Excluir
                            </Button>
                        }

                        <Button onClick={submit} actionFeedbackType='save'>

                            Salvar
                        </Button>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default LaneAliasForm;